import { useContext, useMemo } from 'react';
import { getBezierPath, Position, useStore, type Edge } from 'reactflow';
import { brandColors } from '@duke-hq/ui/theme/chakraTheme';
import { ActiveNodeContext } from '../FlowEditorStateContext';
import { findOffscreenEdges } from '../utils';

// Draws the edges React Flow drops under `onlyRenderVisibleElements`: an
// off-screen node never mounts, so its edges are culled and the node it connects
// to looks like a dead-end. Renders inside the viewport so it pans and zooms with
// the canvas, and only fills in the dropped edges (React Flow keeps the rest).
export function OffscreenEdges({ edges }: { edges: Edge[] }) {
  const nodeInternals = useStore((s) => s.nodeInternals);
  const transform = useStore((s) => s.transform);
  const activeNode = useContext(ActiveNodeContext);

  const offscreenEdges = useMemo(
    () => findOffscreenEdges(nodeInternals, edges),
    [nodeInternals, edges],
  );

  if (offscreenEdges.length === 0) {
    return null;
  }

  const [translateX, translateY, zoom] = transform;

  return (
    <svg
      // Apply the viewport transform so paths live in flow coordinates like React
      // Flow's own edges. Pointer events off so the overlay never intercepts clicks.
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        overflow: 'visible',
        pointerEvents: 'none',
        transform: `translate(${translateX}px, ${translateY}px) scale(${zoom})`,
        transformOrigin: '0 0',
      }}
    >
      {offscreenEdges.map((edge) => {
        const [path] = getBezierPath({
          sourceX: edge.sourceX,
          sourceY: edge.sourceY,
          sourcePosition: Position.Right,
          targetX: edge.targetX,
          targetY: edge.targetY,
          targetPosition: Position.Left,
        });

        const isConnectedToActiveNode =
          activeNode != null && (edge.source === activeNode.id || edge.target === activeNode.id);

        return (
          <path
            key={edge.id}
            d={path}
            fill="none"
            stroke={isConnectedToActiveNode ? brandColors.brand[500] : brandColors.gray[400]}
            strokeWidth={isConnectedToActiveNode ? 2.5 : 1}
          />
        );
      })}
    </svg>
  );
}
